import { getAllConnectedAppsData } from 'api/ApiHooks/BigcommerceHubspotApiHooks';
import LoadingScreen from 'components/LoadingScreen';
import useAuth from 'hooks/useAuth';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import { ReactNode, createContext, useState, useEffect } from 'react';
import { useMutation } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import axios from 'utils/axios';

export type ConnectedAppsContextProps = {
  bigcommerceSourceShopId: string;
  bigcommerceDomain: string;
  hubspotTargetShopId: string;
  hubspotPortalId: number;
  hubspotPortalUser: string;
  hubspotOnboarded: boolean;
  refetchConnectedAddData?: () => void;
};

type BigcommerceInstalled = {
  _id: string;
  domain: string;
  id: string;
};

type HubspotInstalled = {
  _id: string;
  hub_id: number;
  onboarded?: boolean;
  user: string;
  is_connected: boolean;
};

export type ConnectedAppsDataType = {
  success: boolean;
  message: string;
  code?: string;
  ip?: string;
  data: {
    bigcommerce: {
      installed: BigcommerceInstalled[];
    };
    hubspot: {
      installed: HubspotInstalled[];
    };
  };
};

const initialState: ConnectedAppsContextProps = {
  bigcommerceSourceShopId: '',
  bigcommerceDomain: '',
  hubspotTargetShopId: '',
  hubspotPortalId: 0,
  hubspotPortalUser: '',
  hubspotOnboarded: false,
};

const ConnectedAppsContext = createContext(initialState);

type ConnectedAppsProviderProps = {
  children: ReactNode;
};

function ConnectedAppsProvider({ children }: ConnectedAppsProviderProps) {
  const [marketplace, setMarketplace] = useState({
    bigcommerceSourceShopId: '',
    bigcommerceDomain: '',
    hubspotTargetShopId: '',
    hubspotPortalId: 0,
    hubspotPortalUser: '',
    hubspotOnboarded: false,
  });

  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  let accessToken = searchParams.get('user_token');
  if (accessToken == null) {
    accessToken = localStorage.getItem('accessToken');
  }

  const mutation = useMutation(
    () => getAllConnectedAppsData(accessToken ?? ''),
    {
      onError: () => {
        delete axios.defaults.headers.common['Ced-Source-Id'];
        delete axios.defaults.headers.common['Ced-Target-Id'];
        enqueueSnackbar(t('something_went_wrong'), {
          variant: 'error',
        });
      },
      onSuccess: data => {
        if (data.success) {
          const count = data.data.hubspot.installed.length;
          axios.defaults.headers.common['Ced-Source-Id'] =
            data.data.bigcommerce.installed?.[0]._id ?? '';
          axios.defaults.headers.common['Ced-Target-Id'] = count
            ? data.data.hubspot.installed[count - 1]._id
            : '';
          localStorage.setItem(
            'storeHash',
            data.data.bigcommerce.installed?.[0]?.id
          );
          const connectedTarget = data.data.hubspot?.installed.filter(
            connected => connected.is_connected
          );
          setMarketplace({
            bigcommerceSourceShopId:
              data.data.bigcommerce.installed?.[0]._id ?? '',
            bigcommerceDomain:
              data.data.bigcommerce.installed?.[0].domain ?? '',
            hubspotTargetShopId: count ? connectedTarget[0]._id : '',
            hubspotPortalId: Number(count ? connectedTarget[0].hub_id : 0),
            hubspotPortalUser: count ? connectedTarget[0].user : '',
            hubspotOnboarded: count
              ? connectedTarget[0]?.onboarded ?? false
              : false,
          });
        } else {
          delete axios.defaults.headers.common['Ced-Source-Id'];
          delete axios.defaults.headers.common['Ced-Target-Id'];
          enqueueSnackbar(data.message, {
            variant: 'error',
          });
        }
      },
    }
  );

  useEffect(() => {
    isAuthenticated && mutation.mutate();
  }, [isAuthenticated]);

  if (mutation.isLoading) {
    return <LoadingScreen />;
  }

  return (
    <ConnectedAppsContext.Provider
      value={{
        bigcommerceSourceShopId: marketplace.bigcommerceSourceShopId,
        bigcommerceDomain: marketplace.bigcommerceDomain,
        hubspotTargetShopId: marketplace.hubspotTargetShopId,
        hubspotPortalId: marketplace.hubspotPortalId,
        hubspotPortalUser: marketplace.hubspotPortalUser,
        hubspotOnboarded: marketplace.hubspotOnboarded,
        refetchConnectedAddData: mutation.mutate,
      }}
    >
      {children}
    </ConnectedAppsContext.Provider>
  );
}

export { ConnectedAppsProvider, ConnectedAppsContext };
